import * as React from 'react';
// Import Libraries
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { RouteComponentProps } from 'react-router';
import { FormGroup, InputGroup, Button, FormControl } from 'react-bootstrap';
// Import App Components
import { Link } from 'react-router-dom';
import errorReporter from '@app/errorReporter';
import PanelHeader from '../react-common/PanelHeader';
import PanelBody from '../react-common/PanelBody';
import Tabs from '../react-common/Tabs';
import Pagination from '../react-common/Pagination';
import AnnouncementList from './AnnouncementList/AnnouncementList';
import { ES_DELAY } from './constants';

const newAnnouncementButton = (
  <Link key={uuidv4()} to="/new">
    <span className="btn btn-success">+ New Announcement</span>
  </Link>
);

// This specify the available tabs
const tabs = [
  { text: 'Previously Sent', icon: 'history' },
  { text: 'Scheduled', icon: 'clock-o' },
  { text: 'Saved Drafts', icon: 'floppy-o' },
];
type validAnnouncementType = 'draft' | 'sent' | 'scheduled';
const INDEX_TYPE_MAP: validAnnouncementType[] = ['sent', 'scheduled', 'draft'];

type AnnouncementListState = {
  searchValue: string;
  activeTabIndex: number;
  announcements: object[];
  count: number;
  limit?: number;
  page: number;
  isLoading: boolean;
};

class AnnouncementListView extends React.Component<
  RouteComponentProps,
  AnnouncementListState
> {
  constructor(props: RouteComponentProps) {
    super(props);
    this.state = {
      searchValue: '',
      activeTabIndex: 0,
      page: 1,
      count: 0,
      announcements: [],
      isLoading: true,
    };
  }

  onTabChange =
    (value: string, index: number) =>
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.stopPropagation();
      this.setState(
        { activeTabIndex: index, announcements: [], page: 1, isLoading: true },
        this.loadAnnouncements,
      );
    };

  loadAnnouncements = async (esDelay?: number) => {
    if (esDelay && esDelay > 0) {
      await new Promise(resolve => setTimeout(resolve, esDelay));
    }

    const { activeTabIndex, page, searchValue } = this.state;
    // Switch type based on active tab
    const type = INDEX_TYPE_MAP[activeTabIndex];
    const {
      data: { announcements, count, limit },
    } = await axios.get('/announcement/all', {
      params: {
        stringQuery: searchValue,
        type,
        page: page - 1,
      },
    });
    this.setState({ announcements, count, limit, isLoading: false });
  };

  onPageChange = (page: number) => {
    this.setState({ page, isLoading: true }, this.loadAnnouncements);
  };

  deleteAnnouncement =
    (announcementId: string) => async (event: React.MouseEvent) => {
      event.stopPropagation();
      this.setState({ isLoading: true });
      try {
        await axios.delete(`/announcement/${announcementId}`);
        this.loadAnnouncements(ES_DELAY);
      } catch (error) {
        this.setState({ isLoading: false });
        errorReporter.error('Failed to delete announcement:', error);
      }
    };

  onSearchInputChange = (event: any) => {
    event.preventDefault();
    const { value: searchValue } = event.currentTarget;
    this.setState({ searchValue });
  };

  searchAnnouncements = () => {
    this.setState({ page: 1, isLoading: true }, this.loadAnnouncements);
  };

  componentDidMount() {
    this.loadAnnouncements();
  }

  render() {
    const {
      activeTabIndex,
      announcements,
      count,
      limit,
      page,
      isLoading,
      searchValue,
    } = this.state;
    // Switch type based on active tab
    const type = INDEX_TYPE_MAP[activeTabIndex];
    return (
      <>
        <PanelHeader
          title="Brokerage Announcements"
          extra={[newAnnouncementButton]}
        />
        <PanelBody>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignContent: 'flex-start',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Tabs
              disabled={isLoading}
              activeTabIndex={activeTabIndex}
              options={tabs}
              onChange={this.onTabChange}
            />
            <hr style={{ width: '100%' }}></hr>
            <section className="announcement-list-content">
              {/* TODO: Break search input into separate component */}
              <FormGroup className="announcement-search">
                <InputGroup>
                  <FormControl
                    onChange={this.onSearchInputChange}
                    value={searchValue}
                    placeholder="Search Announcements"
                    type="text"
                  />
                  <InputGroup.Button>
                    <Button
                      onClick={this.searchAnnouncements}
                      bsStyle="primary"
                    >
                      <i className="fa fa-search" />
                    </Button>
                  </InputGroup.Button>
                </InputGroup>
              </FormGroup>
              <Pagination
                totalItems={count}
                itemsPerPage={limit}
                maxSize={5}
                currentPage={page}
                onChange={this.onPageChange}
                disabled={isLoading}
              />
              <AnnouncementList
                deleteAnnouncement={this.deleteAnnouncement}
                isLoading={isLoading}
                type={type}
                announcements={announcements}
              />
            </section>
          </div>
        </PanelBody>
      </>
    );
  }
}

export default AnnouncementListView;
